import { BulkFile } from '../../../cms/models/BulkFile'
import { setRecoil } from '../../../state/recoilNexus'
import { cacheBuster } from '../../../state/state'
import { Lists } from '../../../utils/lists'
import {
	NodesMap,
	resetTree
} from '../../Form/renderers/components/ProductDataSelect/utils'
import {
	BulkTableColumnsState,
	BulkTableFilter,
	BulkUploadStatus,
	SuccessfulBulkFileMessage
} from '../state'
import { Strings } from '../../../utils/strings'
import { fi } from '../../../utils/helpers'

/**
 * Manages the in-memory collection of BulkFiles and provides
 * CRUD operations with cache invalidation side-effects.
 */
export const BulkFileStore = {
	/**
	 * Returns the current files array (read-only access point for consumers).
	 */
	getAll(): BulkFile[] {
		return BulkFile.files
	},

	/**
	 * Initialize the store from raw API data.
	 */
	initialize(list: any[]): void {
		BulkFile.files = list.map((f) => new BulkFile(f))
		Lists.sort(BulkFile.files, 'filename')
		setRecoil(cacheBuster(`bulk-file-all`), (val) => val + 1)
	},

	/**
	 * Add a new file or update an existing one by filename match.
	 * Calls the callback with the existing entry if found.
	 */
	addOrUpdate(item: BulkFile, cb: (existing: BulkFile) => void): BulkFile {
		let idx = BulkFile.files.findIndex((f) => f.filename === item.filename)
		if (idx === -1) {
			BulkFile.files.push(item)
			Lists.sort(BulkFile.files, 'filename')
			setRecoil(cacheBuster(`bulk-file-all`), (val) => val + 1)
			resetTree(NodesMap[item.filename])
			return item
		} else {
			const existing = BulkFile.files[idx]
			cb(existing)
			BulkFile.files[idx] = existing
			return existing
		}
	},

	/**
	 * Remove files by bulk IDs from the in-memory store.
	 */
	removeByIds(ids: number[]): void {
		BulkFile.files = BulkFile.files.filter((f) => !ids.includes(f.bulkid))
		setRecoil(cacheBuster(`bulk-file-all`), (val) => val + 1)
	},

	/**
	 * Clear all files from the store (used on unmount).
	 */
	clear(): void {
		BulkFile.files = []
	},

	/**
	 * Return files matching the given filter criteria.
	 */
	filteredFiles(filter: BulkTableFilter): BulkFile[] {
		const { statuses } = filter

		if (statuses.length === 0) {
			return BulkFile.files
		}

		return BulkFile.files.filter((f) => {
			if (statuses.includes(SuccessfulBulkFileMessage) && !f.errorMessages) {
				return f
			}

			return statuses.some(
				(err) => f.formattedErrors.includes(err) || f.error === err
			)
		})
	},

	/**
	 * Collect all unique error strings across all files.
	 */
	errorList(): string[] {
		return Array.from(
			new Set(
				BulkFile.files
					.map((f) => {
						const err: string[] = []
						if (f.error) {
							err.push(f.error)
						}
						err.push(...f.formattedErrors)
						return err
					})
					.flat()
			)
		)
	},

	/**
	 * Compute upload status summary.
	 */
	uploadStatus(): BulkUploadStatus {
		let errors = 0
		let isUploading = false
		const filesLength = BulkFile.files.length
		BulkFile.files.forEach((f) => {
			if (Object.keys(f.errors).length || f.error) {
				errors++
			}
			if (f.uploadInfo) {
				isUploading = true
			}
		})
		return { total: filesLength, successful: filesLength - errors, isUploading, errors  }
	},

	/**
	 * Generate a CSV-ready report from files.
	 */
	generateReport(files: BulkFile[], fields: BulkTableColumnsState[]): any[] {
		const reportData: any[] = []
		const headers: string[] = fields.map((field) => field.label)

		headers.push('Status')
		reportData.push(headers)

		for (const file of files) {
			const row: string[] = []
			fields
				.filter((field) => field.visible === true)
				.forEach((field) => {
					let rowValue = Strings.default(file[field.name], 'Not set')

					if (field.name === 'file') {
						rowValue = Strings.default(file['filename'], 'No filename')
					}
					row.push(`"${rowValue}"`)
				})

			if (file.error) {
				row.push(file.error)
			} else {
				row.push(Strings.default(file.formattedErrors.join(' '), 'Ok'))
			}
			reportData.push(row)
		}

		return reportData
	}
}
